import type {
  AccountAllocationGridAccount,
  AccountAllocationGridHolding,
  AccountAllocationGridReport,
} from '@components/AccountAllocationGrid/types';
import { AccountTypes } from '@interfaces/account';
import { enumValues } from '@utils/enumValues';
import type { EditAccountAllocationsPortfolioReport } from './editAccountAllocations.queries';

type ReportAccount =
  EditAccountAllocationsPortfolioReport['subAccounts'][number];
type ReportHolding = ReportAccount['holdings'][number];

const nullToUndefined = <T>(value: T | null | undefined) => value ?? undefined;

const toAccountType = (type: string) =>
  enumValues(AccountTypes).find((accountType) => accountType === type);

const toAssetClass = (
  assetClass: ReportHolding['assetClass'],
): AccountAllocationGridHolding['assetClass'] =>
  assetClass
    ? {
        id: assetClass.id,
        name: assetClass.name,
        translatedName: {
          en: nullToUndefined(assetClass.translatedName.en),
          fr: nullToUndefined(assetClass.translatedName.fr),
          es: nullToUndefined(assetClass.translatedName.es),
        },
      }
    : undefined;

const toModelPortfolio = (
  modelPortfolio: EditAccountAllocationsPortfolioReport['modelPortfolio'],
): AccountAllocationGridReport['modelPortfolio'] =>
  modelPortfolio.strategy ? { strategy: modelPortfolio.strategy } : undefined;

const toHolding = (holding: ReportHolding): AccountAllocationGridHolding => ({
  expectedPercentage: holding.expectedPercentage,
  isCashEquivalent: nullToUndefined(holding.isCashEquivalent),
  customExpectedPercentage: nullToUndefined(holding.customExpectedPercentage),
  assetClass: toAssetClass(holding.assetClass),
  assetClassPercentage: nullToUndefined(holding.assetClassPercentage),
  assetClassGroupPercentage: nullToUndefined(holding.assetClassGroupPercentage),
  financialProduct: {
    id: holding.financialProduct.id,
    ticker: nullToUndefined(holding.financialProduct.ticker),
    isCash: nullToUndefined(holding.financialProduct.isCash),
    translatedName: {
      en: nullToUndefined(holding.financialProduct.translatedName.en),
      fr: nullToUndefined(holding.financialProduct.translatedName.fr),
      es: nullToUndefined(holding.financialProduct.translatedName.es),
    },
  },
});

const withProduct = <T extends ReportHolding>(holdings: T[]) =>
  holdings.filter((holding) => Boolean(holding.financialProduct.id));

const toHoldings = (holdings: ReportHolding[]) =>
  withProduct(holdings).map(toHolding);

// Only household holdings carry an actual weight: holding-service rolls each
// fund's market value up across the tradeable accounts.
const toHouseholdHoldings = (
  holdings: EditAccountAllocationsPortfolioReport['holdings'],
): AccountAllocationGridHolding[] =>
  withProduct(holdings).map((holding) => ({
    ...toHolding(holding),
    actualPercentage: holding.actualPercentage,
  }));

const toAccount = (account: ReportAccount): AccountAllocationGridAccount => ({
  id: account.id,
  marketValueCents: account.marketValueCents,
  isTradeable: account.isTradeable,
  subAccount: {
    id: account.subAccount.id,
    state: account.subAccount.state,
    account: {
      id: account.subAccount.account.id,
      custodianAccountNumber: nullToUndefined(
        account.subAccount.account.custodianAccountNumber,
      ),
      type: toAccountType(account.subAccount.account.type),
      user: {
        firstName: nullToUndefined(account.subAccount.account.user.firstName),
        lastName: nullToUndefined(account.subAccount.account.user.lastName),
        entityName: nullToUndefined(account.subAccount.account.user.entityName),
      },
    },
  },
  holdings: toHoldings(account.holdings),
});

export const toAccountAllocationGridReport = (
  report: EditAccountAllocationsPortfolioReport | null | undefined,
): AccountAllocationGridReport | null => {
  if (!report) return null;
  if (report.subAccounts.length === 0) return null;

  return {
    id: report.id,
    allocationMethodology: report.allocationMethodology,
    expectedCashPercentage: report.expectedCashPercentage,
    modelPortfolio: toModelPortfolio(report.modelPortfolio),
    holdings: toHouseholdHoldings(report.holdings),
    subAccounts: report.subAccounts.map(toAccount),
  };
};
